import DOMPurify from 'dompurify';
import React from 'react';

/**
 * Configuração de sanitização para diferentes contextos
 */
const SANITIZE_CONFIG = {
  // Configuração padrão - permite HTML básico mas remove scripts
  default: {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'span', 'div',
      'table', 'thead', 'tbody', 'tr', 'td', 'th', 'a', 'img'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'style',
      'width', 'height', 'target', 'rel'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
  },
  
  // Configuração para visualização de emails (mais permissiva para renderização)
  email: {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'b', 'i', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'img', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'blockquote', 'code', 'pre', 'hr', 'center', 'font', 'small', 'big', 'sub', 'sup'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height', 'style',
      'class', 'id', 'align', 'valign', 'bgcolor', 'color', 'size', 'face', 'border',
      'cellpadding', 'cellspacing', 'colspan', 'rowspan'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button', 'iframe', 'frame'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onsubmit', 'onchange']
  },
  
  // Configuração permissiva para templates (permite mais HTML)
  template: {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'span', 'div',
      'table', 'thead', 'tbody', 'tr', 'td', 'th', 'a', 'img', 'hr',
      'section', 'article', 'header', 'footer', 'nav', 'aside'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'style',
      'width', 'height', 'target', 'rel', 'data-*'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
  }
};

/**
 * Sanitiza HTML usando DOMPurify com configuração específica
 * @param html - Conteúdo HTML a ser sanitizado
 * @param config - Tipo de configuração ('default', 'email', 'template')
 * @returns HTML sanitizado
 */
export const sanitizeHTML = (html: string, config: keyof typeof SANITIZE_CONFIG = 'default'): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    const sanitizeConfig = SANITIZE_CONFIG[config];
    
    return DOMPurify.sanitize(html, {
      ...sanitizeConfig,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_TRUSTED_TYPE: false
    });
  } catch (error) {
    console.error('Erro ao sanitizar HTML:', error);
    // Em caso de erro, retorna string vazia para segurança
    return '';
  }
};

/**
 * Sanitiza HTML para contexto de email (mais restritivo)
 * @param html - Conteúdo HTML a ser sanitizado
 * @returns HTML sanitizado para email
 */
export const sanitizeEmailHTML = (html: string): string => {
  return sanitizeHTML(html, 'email');
};

/**
 * Sanitiza HTML para templates (mais permissivo)
 * @param html - Conteúdo HTML a ser sanitizado
 * @returns HTML sanitizado para templates
 */
export const sanitizeTemplateHTML = (html: string): string => {
  return sanitizeHTML(html, 'template');
};

/**
 * Verifica se o HTML contém scripts ou elementos perigosos
 * @param html - Conteúdo HTML a ser verificado
 * @returns true se contém elementos perigosos
 */
export const hasDangerousContent = (html: string): boolean => {
  if (!html || typeof html !== 'string') {
    return false;
  }

  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe\b/gi,
    /<object\b/gi,
    /<embed\b/gi
  ];

  return dangerousPatterns.some(pattern => pattern.test(html));
};

/**
 * Componente React para renderizar HTML sanitizado
 * @param html - Conteúdo HTML a ser renderizado
 * @param config - Tipo de configuração de sanitização
 * @param className - Classes CSS adicionais
 * @returns Elemento div com HTML sanitizado
 */
export const SanitizedHTML: React.FC<{
  html: string;
  config?: keyof typeof SANITIZE_CONFIG;
  className?: string;
}> = ({ html, config = 'default', className = '' }) => {
  const sanitizedContent = sanitizeHTML(html, config);
  
  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};
